<template>
  <Modal
    class="modalImportTemplate"
    :show-modal="showImportTemplateModal"
    @close="toggleImportTemplateModal"
  >
    <template #header>
      <TitleRegular title="Templates" />
      <TextSmall>
        {{ $t('shared.content_generation.manage_templates') }}
      </TextSmall>
    </template>
    <template #body>
      <div class="table">
        <CaasTableHeader :templates-table="true" />
        <CaasTableRow
          v-for="(temp, index) in templates"
          :key="index"
          :templates-table="true"
          :row-items="temp"
          :allow-edit-template="false"
          :allow-export-template="false"
          @editTemplate="applyTemp"
        >
          <template #applyTemplateBtn>
            <div class="table__applyTemplateBtn" @click="applyTemp(temp)">
              <TextSmall>
                {{ $t('shared.content_generation.apply_template') }}
              </TextSmall>
            </div>
          </template>
        </CaasTableRow>
      </div>
      <PaginationPerPage
        v-if="pages.current"
        :per-page="pages.perPage"
        :current-page="pages.current"
        :total-pages="pages.total"
        @onChange="onPageChange"
        @changePerPage="onPerPageChange"
      />
    </template>
    <template #footer>
      <BtnsWrapperVertical>
        <BtnSecondary
          :title="$t('shared.button.cancel')"
          type="button"
          @click="toggleImportTemplateModal"
        />
      </BtnsWrapperVertical>
    </template>
  </Modal>
</template>
<script setup>
import CaasTableHeader from '@/components/contentGeneration/CaasTable/CaasTableHeader'
import CaasTableRow from '@/components/contentGeneration/CaasTable/CaasTableRow'
import BtnSecondary from '@/components/shared/btn/BtnSecondary'
import BtnsWrapperVertical from '@/components/shared/btn/BtnsWrapperVertical'
import TextSmall from '@/components/shared/font/text/TextSmall'
import TitleRegular from '@/components/shared/font/title/TitleRegular'
import Modal from '@/components/shared/Modal'
import PaginationPerPage from '@/components/shared/pagination/PaginationPerPage'
import i18n from '@/i18n'
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

const route = useRoute()
const router = useRouter()
const store = useStore()
const $t = i18n.t

const templates = computed(() => store.state.contentGeneration.templates)
const pages = computed(() => store.state.contentGeneration.templatesPages)
const currentTemplate = computed(
  () => store.state.contentGeneration.currentTemplate
)
const showImportTemplateModal = computed(
  () => store.state.modals.showImportTemplateModal
)

const startLoader = () => store.dispatch('loading/startLoader')
const stopLoader = () => store.dispatch('loading/stopLoader')
const getTemplates = (payload) =>
  store.dispatch('contentGeneration/getTemplates', payload)
const getTemplate = (payload) =>
  store.dispatch('contentGeneration/getTemplate', payload)
const toggleImportTemplateModal = () =>
  store.commit('modals/toggleImportTemplateModal')
const applyTemplate = (payload) =>
  store.commit('contentGeneration/applyTemplate', payload)

function onPageChange(pageNum) {
  router
    .push({
      name: 'contentGenerationV2',
      query: {
        page: pageNum,
        perPage: route.query.perPage
      }
    })
    .catch(() => {})
}

function onPerPageChange(perPage) {
  router
    .push({
      name: 'contentGenerationV2',
      query: { page: 1, perPage: perPage }
    })
    .catch(() => {})
}

watch(
  () => route.query,
  async () => {
    if (
      route.meta.baseRoute === 'contentGenerationV2' &&
      showImportTemplateModal.value
    ) {
      await getTemplates({
        currentPage: route.query.page,
        perPage: route.query.perPage
      })
    }
  },
  { deep: true }
)

async function applyTemp(temp) {
  toggleImportTemplateModal()
  startLoader()
  await getTemplate(temp.id)
  applyTemplate(currentTemplate.value)
  stopLoader()
}
</script>
<style lang="scss">
.modalImportTemplate {
  .modal-wrapper {
    max-width: 600px;
    @include desktop() {
      max-width: 1150px;
    }
    .modal-container {
      padding: 2rem 1rem;
      &::-webkit-scrollbar {
        display: none !important; /* Chrome Safari */
      }

      scrollbar-width: none; /* Firefox */
      -ms-overflow-style: none; /* IE 10+ */
      .modal-header {
        text-align: center;
        .textSmall {
          margin-top: 8px;
        }
      }
      .modal-body {
        margin: 2rem 0;
        padding: 0 1rem 1rem;
        background: #f8f9fb;
        border-radius: var(--border-radius);
        .caasTableRows,
        .caasTableHeader__header {
          padding: 8px;
          @include desktop {
            padding: 8px 20px;
          }
        }
        .table {
          display: grid;
          grid-template-columns: 1fr;
          grid-template-rows: repeat(auto-fill, minmax(max-content, 0));
          height: 100% !important;
          padding-bottom: 90px;
          overflow: auto;
          position: relative;
          &__applyTemplateBtn {
            border: 1px solid #2c3fd5;
            border-radius: var(--border-radius);
            padding: 5px;
            width: max-content;
            margin-right: 10px;
            @include desktop() {
              margin-right: 0;
              padding: 6px 10px;
            }
            cursor: pointer;
            .textSmall {
              color: #2c3fd5;
              font-size: 12px;
            }
            &:hover {
              background: #2c3fd5;
              .textSmall {
                color: #ffffff;
              }
            }
          }
          .activeTemplate {
            cursor: not-allowed;
            background: #f3f6fa;
            border: 1px solid transparent;
            &:hover {
              background: #f3f6fa;
              .textSmall {
                color: #2c3fd5;
              }
            }
          }
          .creationDate,
          .createdBy,
          .createdByHeader,
          .creationDateHeader {
            display: none;
            @include desktop() {
              display: initial;
            }
          }
          .templatesGrid {
            grid-template-columns: minmax(100px, 1fr) minmax(100px, 1fr);
            @include desktop() {
              grid-template-columns: minmax(100px, 1fr) 150px minmax(100px, 1fr) 250px;
            }
          }
        }
      }
    }
  }
  .modalDeleteCaasDoc {
    .modal-container {
      width: 600px !important;
      .modal-body {
        margin: 2rem 0;
        padding: 0 2.5rem;
        background: initial;
      }
    }
  }
}
</style>
